import * as React from "react";
import {withRouter} from "react-router-dom";
import {RouteComponentProps} from "react-router";
import {none, Option} from "ts-option";
import Lead from "../../model/lead/Lead";
import {connect, Dispatch} from "react-redux";
import {RootState} from "../../store/reducer/root/RootState";
import Routes from "../../navigation/route/Route";
import QuoteDetails from "../../model/quote/QuoteDetails";
import Toast from "../helper/Toast";
import IndividualContact from "../../model/contact/IndividualContact";
import {getIndividualContact} from "../../store/view/contact";
import {getLeadByIdOrRefNo} from "../../store/view/lead";
import Job from "../../model/job/Job";
import {getJobByIdOrRefNumber} from "../../store/view/job";
import {notifyBugsnagAndShowToast} from "../../utils/Utils";
import {OnFailure, OnSuccess} from "../../store/action/actionHelpers";
import {fetchJobByRefNumber} from "../../store/action/job/job";
import {fetchLeadByRefNo} from "../../store/action/lead/lead";
import FinanceProduct from "../../model/financeProduct/FinanceProduct";
import {fetchQuoteDetails, updateQuoteFinanceProduct} from "../../store/action/quote/quote";
import {getQuoteDetails} from "../../store/view/quote";
import "../accounting/common/commonDetailsSummary.scss";
import "./quoteDetailsView.scss";
import QuoteDetailsBody from "./QuoteDetailsBody";

interface RouteProps {
	/**
	 * Job reference number.
	 */
	jobReferenceNumber: string;
	/**
	 * Lead reference number.
	 */
	leadReferenceNumber: string;
	/**
	 * Quote identifier.
	 */
	quoteId: string;
}

interface ReduxProps {
	/**
	 * Quote details data.
	 */
	quoteDetails: Option<QuoteDetails>;
	/**
	 * Job data.
	 */
	job: Option<Job>;
	/**
	 * Lead data.
	 */
	lead: Option<Lead>;
	/**
	 * Lead contact data.
	 */
	contact: Option<IndividualContact>;
	/**
	 * Finance products.
	 */
	financeProducts: Option<FinanceProduct[]>;
}

interface DispatchProps {
	/**
	 * Fetch quote details action.
	 */
	fetchQuoteDetails: (quoteId: string, onFailure: OnFailure) => void;
	fetchJobByRefNumber: (jobRefNumber: string, onSuccess: OnSuccess, onFailure: OnFailure) => void;
	fetchLeadByRefNumber: (leadRefNumber: string, onSuccess: OnSuccess, onFailure: OnFailure) => void;
	/**
	 * Update quote finance product action.
	 * @param quoteId
	 * @param financeProductId
	 */
	updateQuoteFinanceProduct: (quoteId: string, financeProductId: string) => void;
}

interface State {
	tooltipQuoteItemIndex: Option<number>;
	summaryInfoTooltipVisible: boolean;
}

type Props = ReduxProps & DispatchProps & RouteComponentProps<RouteProps>;

/**
 * Quote details view.
 */
class QuoteDetailsView extends React.PureComponent <Props, State> {

	quoteItemInfoRef: HTMLDivElement[] = [];
	summaryInfoParentRef: HTMLDivElement;

	constructor(props: Props) {
		super(props);
		this.state = {
			tooltipQuoteItemIndex: none,
			summaryInfoTooltipVisible: false
		};
	}

	componentDidMount(): void {
		const quoteId = this.props.match.params.quoteId;
		this.props.fetchQuoteDetails(quoteId, (error) => {
			notifyBugsnagAndShowToast(error, 'Quote details');
			this.goToPreviousView();
		});
		if (this.props.match.params.jobReferenceNumber && this.props.job.isEmpty) {
			this.props.fetchJobByRefNumber(this.props.match.params.jobReferenceNumber, () => {},
				(err) => notifyBugsnagAndShowToast(err, "Fetch job"))
		}
		if (this.props.match.params.leadReferenceNumber && this.props.lead.isEmpty) {
			this.props.fetchLeadByRefNumber(this.props.match.params.leadReferenceNumber, () => {},
				(err) => notifyBugsnagAndShowToast(err, "Fetch lead"))
		}
	}

	render() {
		const refNumPathItem1 = this.props.lead.map(l => `Lead ID# ${l.referenceNumber}`).getOrElse(
			this.props.job.map(j => `Job ID# ${j.referenceNumber}`).orNull);
		const isDataLoading = this.props.lead.isEmpty && this.props.job.isEmpty;
		return (
			<QuoteDetailsBody contact={this.props.contact}
							  isLoading={isDataLoading}
							  onBack={this.goToPreviousView}
							  refNumPathItem1={refNumPathItem1}
							  quoteDetails={this.props.quoteDetails}/>
		);
	}

	goToPreviousView = () => {
		const params = this.props.match.params;
		this.props.history.push(
			this.props.lead.isDefined ? Routes.EditLead(params.leadReferenceNumber) : Routes.EditJob(params.jobReferenceNumber)
		);
	};
}

function mapStateToProps(state: RootState, props: Props) {
	const quoteDetails = getQuoteDetails(props.match.params.quoteId, state);
	const lead = getLeadByIdOrRefNo(props.match.params.leadReferenceNumber, state);
	const job = getJobByIdOrRefNumber(props.match.params.jobReferenceNumber, state);
	const contactId = lead.isDefined ? lead.map(l => l.data.principalContactId) : job.map(j => j.data.principalContactId);
	const contact = contactId.flatMap(id => getIndividualContact(id, state));
	const financeProducts = state.financeProduct.financeProducts;
	return ({quoteDetails, job, lead, contact, financeProducts});
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		fetchQuoteDetails: (quoteId: string, onFailure: OnFailure) => dispatch(fetchQuoteDetails(quoteId, () => {}, onFailure)),
		fetchJobByRefNumber: (jobRefNumber: string, onSuccess: OnSuccess, onFailure: OnFailure) => dispatch(
			fetchJobByRefNumber(jobRefNumber, onSuccess, onFailure)),
		fetchLeadByRefNumber: (jobRefNumber: string, onSuccess: OnSuccess, onFailure: OnFailure) => dispatch(
			fetchLeadByRefNo(jobRefNumber, onSuccess, onFailure)),
		updateQuoteFinanceProduct: (quoteId: string, financeProductId: string) => dispatch(
			updateQuoteFinanceProduct(
				quoteId,
				financeProductId,
				() => Toast.success("Finance product has been set.", "Set finance product"),
				(error) => notifyBugsnagAndShowToast(error, "Set finance product"))
		)
	}
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(QuoteDetailsView))
